import { listRfqExcelFiles, RFQ_FILES_BUCKET } from '../../../../api/rfqFiles';
import { listStorageObjects } from '../../../../api/storage';
import { repairRfqFileSize } from '../../../../services/rfqItems';

export interface ExcelFileInfo {
  filename: string;
  filePath: string;
  size: number | null;
  lastModified: string | null;
  exists: boolean;
  source: 'database' | 'pattern_check';
}

export type ExcelFileListing =
  | { error: unknown }
  | { files: ExcelFileInfo[]; missingFiles: string[]; fixedSizes: { filename: string; size: number }[] };

/**
 * An RFQ's stored Excel files, each checked against Storage. A row whose
 * object is gone is listed as missing; a row with no size takes the size from
 * Storage and the backend repairs it (audited, reading the same metadata). No
 * file at all lists one placeholder.
 */
export async function listVerifiedExcelFiles(rfqId: number): Promise<ExcelFileListing> {
  const { data: dbFiles, error } = await listRfqExcelFiles(rfqId);
  if (error) return { error };

  const files: ExcelFileInfo[] = [];
  const missingFiles: string[] = [];
  const fixedSizes: { filename: string; size: number }[] = [];

  if (dbFiles && dbFiles.length > 0) {
    for (const file of dbFiles) {
      let fileExists = true;
      let actualSize = file.file_size;

      try {
        if (!file.storage_path) {
          missingFiles.push(file.filename);
          continue;
        }
        const pathParts = file.storage_path.split('/');
        const directory = pathParts.slice(0, -1).join('/') || '';
        const filename = pathParts[pathParts.length - 1];

        const { data: storageFiles, error: listError } = await listStorageObjects(RFQ_FILES_BUCKET, directory, filename);

        if (listError || !storageFiles || storageFiles.length === 0) {
          fileExists = false;
          missingFiles.push(file.filename);
          console.warn(`File not found in storage: ${file.storage_path}`);
        } else {
          const storageFile = storageFiles[0];

          // If database size is missing but we have it from storage, fix it
          if (!file.file_size && storageFile.metadata?.size) {
            const storageSize: number = storageFile.metadata.size;
            actualSize = storageSize;
            fixedSizes.push({
              filename: file.filename,
              size: storageSize
            });

            // This listing runs every time the preview opens, and repairs
            // a missing rfq_files.file_size. The backend writes it (audited),
            // reading the size from the same Storage metadata itself.
            const { error: updateError } = await repairRfqFileSize(file.id);

            if (updateError) {
              console.error(`Failed to update file size for ${file.filename}:`, updateError);
            }
          }
        }
      } catch (verifyError) {
        console.warn(`Error verifying file ${file.filename}:`, (verifyError as { message?: string }).message);
        fileExists = false;
      }

      files.push({
        filename: file.filename,
        filePath: file.storage_path || file.file_path || '',
        size: actualSize,
        lastModified: file.updated_at || file.created_at,
        exists: fileExists,
        source: 'database'
      });
    }
  }

  // If no database files found, add placeholder
  if (files.length === 0) {
    files.push({
      filename: `No Excel files found for RFQ ${rfqId}`,
      filePath: '',
      size: null,
      lastModified: null,
      exists: false,
      source: 'pattern_check'
    });
  }

  return { files, missingFiles, fixedSizes };
}
